import { useEffect } from "react";

/** Injects SEO tags (title, description, canonical, JSON-LD) for a page. */
export const useSeo = ({ title, description, canonical, image, jsonLd, noindex, alternates }) => {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content || "");
      return el;
    };

    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
    }
    if (title) {
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("name", "twitter:image", image);
      setMeta("name", "twitter:card", "summary_large_image");
    }
    if (canonical) setMeta("property", "og:url", canonical);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    let robots;
    if (noindex) {
      robots = setMeta("name", "robots", "noindex, follow");
    } else {
      document.head.querySelector('meta[name="robots"]')?.remove();
    }

    const altLinks = [];
    if (alternates?.length) {
      document.head.querySelectorAll('link[rel="alternate"][data-seo="1"]').forEach((el) => el.remove());
      alternates.forEach(({ hreflang, href }) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = hreflang;
        link.href = href;
        link.dataset.seo = "1";
        document.head.appendChild(link);
        altLinks.push(link);
      });
    }

    return () => {
      document.title = previousTitle;
      if (script) script.remove();
      if (robots) robots.remove();
      altLinks.forEach((el) => el.remove());
    };
  }, [title, description, canonical, image, jsonLd, noindex, alternates]);
};
